import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Crown } from 'lucide-react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@llura/ui';

import { useStrings } from '../../hooks/useStrings';

const SPARKLE_COUNT = 18;
const SPARKLE_RADIUS = 130;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Outward sparkle burst — fires when `play` toggles to true. Sits behind
 * the crown via absolute positioning, pointer-events-none. Reduced-motion
 * users get a single calm static ring instead.
 */
function ParticleBurst({ play }: { play: boolean }) {
  const reduced = useMemo(prefersReducedMotion, []);
  const sparkles = useMemo(
    () =>
      Array.from({ length: SPARKLE_COUNT }).map((_, i) => {
        const angle = (i / SPARKLE_COUNT) * Math.PI * 2;
        return {
          x: Math.cos(angle) * SPARKLE_RADIUS,
          y: Math.sin(angle) * SPARKLE_RADIUS,
        };
      }),
    [],
  );

  if (reduced) {
    return play ? (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div
          aria-hidden
          className="h-32 w-32 rounded-full border border-accent/40 glow-accent"
        />
      </div>
    ) : null;
  }

  return (
    <div
      key={play ? 'on' : 'off'}
      aria-hidden
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
    >
      <svg width="280" height="280" viewBox="-140 -140 280 280" className="overflow-visible">
        <motion.circle
          cx={0}
          cy={0}
          r={30}
          fill="none"
          stroke="hsl(var(--accent))"
          strokeWidth={1.5}
          initial={{ scale: 0.2, opacity: 0.6 }}
          animate={{ scale: 2.5, opacity: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        />
        {sparkles.map((s, i) => (
          <motion.circle
            key={i}
            cx={0}
            cy={0}
            r={2.4}
            fill="hsl(var(--accent))"
            initial={{ x: 0, y: 0, opacity: 0, scale: 0.3 }}
            animate={{ x: s.x, y: s.y, opacity: [0, 1, 0], scale: [0.3, 1, 0.4] }}
            transition={{ duration: 1.1, delay: 0.15 + i * 0.01, ease: 'easeOut' }}
          />
        ))}
      </svg>
    </div>
  );
}

interface PointsAwardedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  points: number;
  coachName: string | null;
  onContinue?: () => void;
}

export function PointsAwardedDialog({
  open,
  onOpenChange,
  points,
  coachName,
  onContinue,
}: PointsAwardedDialogProps) {
  const [shown, setShown] = useState(0);
  const { strings, interp } = useStrings();

  useEffect(() => {
    if (!open) {
      setShown(0);
      return;
    }
    const start = performance.now();
    const dur = 900;
    let raf = 0;
    const tick = () => {
      const elapsed = performance.now() - start;
      const t = Math.min(1, elapsed / dur);
      setShown(Math.round(t * points));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, points]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-accent/50">
        <DialogHeader className="items-center text-center">
          <div className="relative mx-auto mb-2 flex h-16 w-16 items-center justify-center">
            <ParticleBurst play={open} />
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 220, damping: 14 }}
              className="relative flex h-16 w-16 items-center justify-center rounded-full bg-accent text-accent-foreground glow-accent"
            >
              <Crown className="h-7 w-7" />
            </motion.div>
          </div>
          <DialogTitle className="font-serif text-3xl">{strings.pointsDialog.title}</DialogTitle>
          <DialogDescription>
            {coachName
              ? interp(strings.pointsDialog.description, { name: coachName })
              : strings.pointsDialog.descriptionFallback}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-1 py-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            {strings.pointsDialog.unit}
          </div>
          <motion.div
            key={shown}
            initial={{ scale: 0.96 }}
            animate={{ scale: 1 }}
            className="font-serif text-6xl text-accent drop-shadow-[0_0_18px_hsl(var(--accent)/0.55)]"
          >
            +{shown}
          </motion.div>
        </div>

        <DialogFooter className="sm:justify-center">
          <Button
            onClick={() => {
              onOpenChange(false);
              onContinue?.();
            }}
            className="bg-accent text-accent-foreground hover:bg-accent/90"
          >
            {strings.common.continueCta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
